import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  TemplateRef,
  computed,
  inject,
  input,
  viewChild
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { concatMap, map, of, Subscription, tap } from 'rxjs';
import { IconComponent } from '../icons/icon.component';
import { RoundIconComponent } from '../round-icon/round-icon.component';
import { RoundIconType } from '../round-icon/round-icon-type.enum';
import { IconNameType } from '../icons/icon-name-type.enum';
import { ExpandableComponent } from '../expandable-component/expandable.component';
import { AccentButtonComponent } from '../accent-button/accent-button.component';
import { CartItemEntryComponent } from '../cart-item-entry/cart-item-entry.component';
import { OrderService } from '../../core/services/order.service';
import { OrderChargeDetailsComponent } from '../order-charge-details/order-charge-details.component';
import { UpsellsComponent } from '../upsells/upsells.component';
import { CouponInputComponent } from '../coupon-input/coupon-input.component';
import { AuthService } from '../../core/services/auth.service';
import { Coupon } from '../../core/models/core/coupon.model';
import { ErrorsService } from '../../core/services/errors.service';
import { AccountCouponsService } from '../../core/services/account/account-coupons.service';
import { OrderedSpecialComponent } from '../ordered-special/ordered-special.component';
import { AdjustmentComponent } from '../adjustment/adjustment.component';
import { AdjustmentType } from '../../core/models/ordering/adjustment-type.enum';
import { SubscriptionSpecialComponent } from '../subscription-special/subscription-special.component';
import { PendingSpecialComponent } from '../pending-special/pending-special.component';
import { OrderWarningComponent } from '../order-warning/order-warning.component';
import { openClose } from '../animations/animations';
import { OrderSpecialService } from '../../core/services/ordering/order-special.service';
import { OrderWarningType } from '../../core/models/ordering/order-warning-type.enum';
import { ConfigService } from '../../core/services/config.service';
import { StoreIdService } from '../../core/services/store-id.service';
import { CheckoutUpsellArea } from '../../core/models/core/checkout-upsell-area.enum';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { LoyaltyIndicatorComponent } from '../loyalty-indicator/loyalty-indicator.component';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { Utils } from '../../core/utils/utils';
import { GTMService } from '../../core/services/gtm.service';
import { EcommerceTriggeringArea } from '../../core/models/ecommerce/ecommerce-triggering-area.enum';
import { EcommerceTriggeringPoint } from '../../core/models/ecommerce/ecommerce-triggering-point.enum';
import { OrderChargeFeeComponent } from '../order-charge-fees/order-charge-fee.component';
import { AuditService } from '../../core/services/audit.service';
import { OrderDetailsButtonComponent } from "../order-details-button/order-details-button.component";
import { CouponConflictComponent } from "../coupon-conflict/coupon-conflict.component";
import { OrderTreeFundraiserComponent } from "../order-tree-fundraiser/order-tree-fundraiser.component";
import { OrderableCoupon } from '../../core/models/ordering/orderable-coupon.model';
import { OrderFundraiserService } from '../../core/services/ordering/order-fundraiser.service';
import { OrderedFundraiser } from '../../core/models/ordering/ordered-fundraiser.model';
import { OrderWarning } from '../../core/models/ordering/order-warning.model';
import { MenuService } from '../../core/services/menu.service';
import { CartSubPromoComponent } from '../cart-sub-promo/cart-sub-promo.component';
import { PremiumService } from '../../loyalty/premium/premium.service';
import { DeployUrlPipe } from '../../core/pipes/deploy-url.pipe';

@Component({
  selector: 'cwo-menu-order-tree',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    RoundIconComponent,
    ExpandableComponent,
    AccentButtonComponent,
    CartItemEntryComponent,
    OrderChargeDetailsComponent,
    UpsellsComponent,
    CouponInputComponent,
    RouterLink,
    OrderedSpecialComponent,
    AdjustmentComponent,
    SubscriptionSpecialComponent,
    PendingSpecialComponent,
    OrderWarningComponent,
    LoyaltyIndicatorComponent,
    TranslocoDirective,
    OrderChargeFeeComponent,
    CurrencyPipe,
    CouponConflictComponent,
    OrderDetailsButtonComponent,
    OrderTreeFundraiserComponent,
    CartSubPromoComponent,
    DeployUrlPipe
],
  templateUrl: './menu-order-tree.component.html',
  styleUrl: './menu-order-tree.component.scss',
  animations: [openClose()],
})
export class MenuOrderTreeComponent implements OnInit, OnDestroy {
  modalService = inject(ModalService);
  orderFundraiserService = inject(OrderFundraiserService);
  orderService = inject(OrderService);
  couponsService = inject(AccountCouponsService);
  authService = inject(AuthService);
  errorsService = inject(ErrorsService);
  accountCouponsService = inject(AccountCouponsService);
  orderSpecialService = inject(OrderSpecialService);
  configService = inject(ConfigService);
  storeIdService = inject(StoreIdService);
  router = inject(Router);
  translocoService = inject(TranslocoService);
  gtmService = inject(GTMService);
  private auditService = inject(AuditService);
  private menuService = inject(MenuService);
  private premiumService = inject(PremiumService);

  fundraiserLoadingSubscription: Subscription | undefined = undefined;
  title = input.required<string>();
  iconNameType = IconNameType;
  roundIconType = RoundIconType;
  orderWarningType = OrderWarningType;
  adjustmentType = AdjustmentType;
  checkoutUpsellArea = CheckoutUpsellArea;
  allErrors?: string[] = [];
  couponWarning: Coupon | null = null;
  displayPendingWarning: boolean = false;
  orderWarnings = computed(() => {    
    return this.orderService.getWarnings(this.orderService.order());
  });

  deliveryDiscountSpecial = this.orderService.deliveryDiscountSpecial;

  orderedSpecials = computed(() => this.orderService.order().orderedSpecials);

  unsubmittedCoupon = computed(() => {
    return this.accountCouponsService.unsubmittedCoupon();
  });

  pendingApplicationCoupon = computed(() => {
    return this.accountCouponsService.pendingApplicationCoupon();
  });

  pendingApplicationCouponAsOrderable = computed((): OrderableCoupon | undefined => {
    const pending = this.accountCouponsService.pendingApplicationCoupon();
    if (!pending) return undefined;
    const coupon = this.coupons().find(c => c.code === pending.code);
    return {
      name: coupon?.name ?? pending.code,
      isCoupon: true,
      percentOff: 0,
      instructions: coupon?.instructions ?? '',
      error: pending.error,
      errorType: 'APPLICATION_FAILED',
    };
  });

  pendingApplicationCouponWarning = computed(() => {
    const pending = this.accountCouponsService.pendingApplicationCoupon();
    if (!pending) return null;
    const coupon = this.coupons().find(c => c.code === pending.code);
    const couponName = coupon?.name ?? pending.code;
    return {
      type: OrderWarningType.ORDER,
      text: pending.error ??
        this.translocoService.translate('modals.coupon_confirmation_modal.coupon_pending_message', { coupon: couponName }),
    };
  });

  orderWarningSpecial = computed(() => {
    return this.orderSpecialService.orderWarningSpecial();
  });

  order = computed(() => this.orderService.order());

  selfOrderWarning = computed<OrderWarning | null>(() => {
    const order = this.order();
    if (!order.isInitialized || !order.isSelfOrderSource) return null;

    const orderType = this.configService.findOrderTypeConfig(order.orderType)?.displayName ?? order.orderType;
    return {
      type: OrderWarningType.ORDER,
      text: this.translocoService.translate('modals.cart.self_order_warning', { orderType }),
    };
  });

  items = computed(() => {
    return this.orderService.order().items ?? [];
  });
  accountState = computed(() => {
    return this.authService.state();
  });
  coupons = computed(() => {
    return this.accountCouponsService.coupons();
  });

  couponAppliedOnMissingOrderWarning =  {
    type: OrderWarningType.MISSING_ORDER,
    text: 'To verify your coupon code you need to complete your order details first',
  };

  hasActiveSub = computed(() => this.premiumService.hasActiveSubscription());

  private readonly orderItemsContainer = viewChild<ElementRef>('orderItemsContainer');
  private readonly couponWarningModalTemplateRef = viewChild.required<TemplateRef<any>>('couponWarningModal');
  private readonly couponInstructionModalTemplateRef = viewChild.required<TemplateRef<any>>('couponInstructionModal');
  private readonly pendingApplicationCouponInstructionModalRef = viewChild.required<TemplateRef<any>>('pendingApplicationCouponInstructionModal');
  private readonly couponInputField = viewChild.required<CouponInputComponent>('couponInputField');

  expanded = false;
  isInMenu = false;

  ngOnInit(): void {
    this.isInMenu = this.router.url.includes('/menu');
  }

  ngOnDestroy(): void {
    const isNavigating = !!this.router.getCurrentNavigation();
    if (isNavigating) {
      this.auditService.createAudit(() => "Check Out button clicked");
    }
  }

  handleExpansion(value: boolean) {
    this.expanded = value;

    if (value) {
      this.scrollToBottom();
    }
  }

  scrollToBottom() {
    const interval = setInterval(() => {
      const orderItemsContainer = this.orderItemsContainer();
      if (orderItemsContainer) {
        orderItemsContainer.nativeElement.scrollTop =
          orderItemsContainer.nativeElement.scrollHeight;
      }
    }, 10);

    setTimeout(() => {
      clearInterval(interval);
    }, 500);
  }

  showAddCouponModal() {
    this.modalService.closeModal().subscribe(() => {
      this.modalService.openModal(ModalName.AddCoupon);
    });
  }

  handleApplyCoupon(value: string) {
    if(value.trim().length === 0) {
      const emptyCouponMessage = 'Coupon code is empty';

      if(!this.allErrors?.includes(emptyCouponMessage) && !this.allErrors?.length) {
        this.allErrors?.push(emptyCouponMessage);
      }
      return;
    }
    
    this.allErrors = [];
    const newCoupon: Coupon = {
      code: value,
    };

    if(this.order().dummy){
      this.accountCouponsService.addUnsubmittedCoupon({code: value, triggeringArea: EcommerceTriggeringArea.ORDER_TREE, triggeringPoint: EcommerceTriggeringPoint.COUPON_INPUT});
      this.couponInputField().couponValue = '';
      return;
    } else {
      this.accountCouponsService.removeUnsubmittedCoupon();
    }

    this.orderService
      .addCoupon(newCoupon, EcommerceTriggeringPoint.COUPON_INPUT, EcommerceTriggeringArea.ORDER_TREE)
      .pipe(concatMap((response) => 
        this.orderService.fetchOrder().pipe(
          map(() => response)
        )
      ))
      .subscribe({
        next: (res) => {

          if(res.body?.errorType === "INCOMPATIBLE" && res.body?.bestCouponCode)  {
            this.handleCouponWarning(res.body)
            return;
          }

          if(res.status === 200) {
            this.auditService.createAudit(() => `Applied Coupon: ${res.body?.code}, Error Type: ${res?.body?.errorType}, Error: ${res.body?.error}`);
            this.auditService.createAudit(() => `Coupon "${res.body?.code}" has been applied to the order": {"couponName":"${res.body?.name}"}`);
            return;
          }

          if(res.status === 201) {
            this.auditService.createAudit(() =>
              `Coupon "${res.body?.code}" has been applied to the order": {"couponName":"${res.body?.name}"}`
            );
            return;
          }
        },
        error: (err) => {
          this.auditService.createAudit(() => `Applied Coupon: ${value}, Error Type: ${err.error?.errorType}, Error: ${err.error?.error}`);
          if (!err.error) {
            this.allErrors = ['Network error'];
          } else {
            const parsedError = this.errorsService.parseErrorResponse(err);
            this.allErrors = parsedError.allErrors;
          }
        },
      });
  }

  
  handleCouponWarning(details: Coupon) {
    this.couponWarning = details;
    this.modalService.openGenericModal({
      modalTitle: this.translocoService.translate('modals.coupon_conflict.title'),
      modalContent: this.couponWarningModalTemplateRef(),
      confirmButtonText: this.translocoService.translate('modals.coupon_conflict.ok'),
    })?.subscribe(()=> this.couponWarning = null);
  }

  handleInstruction({ openModal, warningType }: { openModal: boolean; warningType: string }) {
    this.displayPendingWarning = warningType === OrderWarningType.ORDER;
  
    if (openModal) {
      this.modalService.openGenericModal({
        modalTitle: 'Coupon Details',
        modalContent: this.couponInstructionModalTemplateRef(),
        confirmButtonText: 'Close',
      });
    }
  }

  handlePendingApplicationCouponInstruction() {
    this.modalService.openGenericModal({
      modalTitle: this.translocoService.translate('modals.coupon_confirmation_modal.instructions_modal_title'),
      modalContent: this.pendingApplicationCouponInstructionModalRef(),
      confirmButtonText: this.translocoService.translate('modals.coupon_confirmation_modal.instructions_modal_confirm_button'),
    });
  }

  handleAddOrderDetails(){
    this.modalService.openModal(ModalName.OrderDetails, {showOrderEditor: true});
  }

  goBackToShopping() {
    if (this.isInMenu) {
      this.modalService.closeModal(ModalName.Cart);
    } else {
      this.auditService.createAudit(() => "Start shopping button clicked");
      this.modalService.closeModal(ModalName.Cart);
      this.router.navigate(this.storeIdService.getRoutePathSegments(['menu']));
    }
  }

  clearOrder() {

    const orderInstanceBeforeSerialization = this.order().copy();

    this.modalService.openGenericModal({
      modalTitle: this.translocoService.translate('modals.clear_order_confirmation_modal.confirm_order_clearance'),
      modalMessage: this.translocoService.translate('modals.clear_order_confirmation_modal.confirm_order_clearance_prompt'),
      confirmButtonText: this.translocoService.translate('modals.clear_order_confirmation_modal.yes_clear_it'),
      cancelButtonText: this.translocoService.translate('modals.clear_order_confirmation_modal.no_keep_it'),
      primaryAction: () => {
        const items = Utils.extractItemsFromOrder(this.order());
        return this.orderService.clearOrder(this.order()).pipe(
          tap(() => {
            this.gtmService.pushRemoveFromCartEvent({
              items: items,
              triggeringArea: EcommerceTriggeringArea.ORDER_TREE,
              triggeringPoint: EcommerceTriggeringPoint.CLEAR_ORDER_BUTTON,
            });
          })
        ).subscribe(() => {
          if (orderInstanceBeforeSerialization.noticePeriod !== 0 && !this.router.url.includes(`${this.storeIdService.getStorePathPrefix()}/checkout`)) {
            this.modalService.closeAllModals().subscribe(() => {
              this.modalService.openModal(ModalName.OrderTreeNotification, {removeItem: true});
            });
          }
        });
      },
      secondaryAction: () => of(false)
    })
  }

  startOverSelfOrder() {
    this.modalService.openGenericModal({
      modalTitle: this.translocoService.translate('modals.cart.start_over'),
      modalMessage: this.translocoService.translate('modals.cart.start_over_confirm_message'),
      confirmButtonText: this.translocoService.translate('modals.cart.start_over_confirm_keep'),
      cancelButtonText: this.translocoService.translate('modals.cart.start_over_confirm_clear'),
      primaryAction: () => of(false),
      secondaryAction: () => {
        const order = this.order();

        return this.orderService.deleteOrder().pipe(
          concatMap(() => this.menuService.fetchPanels(order.orderType, order.deferTime ?? '', 'Web')),
          concatMap(() => this.modalService.closeModal(ModalName.Cart)),
          tap(() => {
            this.modalService.openModal(ModalName.OrderDetails, {
              showOrderEditor: true,
            });
          })
        ).subscribe({
          error: (err) => {
            const parsedError = err.error ? this.errorsService.parseErrorResponse(err) : undefined;
            const errorMessage = parsedError?.allErrors.length ? parsedError.allErrors[0] : 'Network error';

            this.modalService.openGenericErrorModal({
              modalMessage: errorMessage,
            });
          },
        });
      },
    });
  }

  removeFundraiser(fundraiser: OrderedFundraiser) {
    if(!fundraiser) return;

    this.fundraiserLoadingSubscription = this.orderFundraiserService.removeFundraiser(fundraiser).subscribe({
      next: () => {
        this.fundraiserLoadingSubscription = undefined;
      }, error: () => {
        this.fundraiserLoadingSubscription = undefined;
      }
    });
  }
}
